import { useEffect } from "react";
import {
  getInitialPaginationRange,
  getStoredItemsPerPage,
} from "tap/utils/paginationPreferences.js";

/**
 * Syncs a paginated screen with its persisted page size on mount.
 * @param {Object} params
 * @param {string} params.storageKey - localStorage key for this component
 * @param {number} params.itemsPerPage - Current Redux page size
 * @param {Function} params.onSync - Callback invoked with fields to update in Redux
 * @param {string} [params.rangeKey] - Optional range field to reset when page size differs
 * @param {string} [params.fieldName="itemsPerPage"] - Redux field to update
 * @param {number} [params.fallback] - Fallback page size when nothing is stored
 */
export function useStoredItemsPerPageSync({
  storageKey,
  itemsPerPage,
  onSync,
  rangeKey,
  fieldName = "itemsPerPage",
  fallback,
}) {
  useEffect(() => {
    const saved = getStoredItemsPerPage(storageKey, fallback);
    if (saved === itemsPerPage) {
      return;
    }

    const updates = { [fieldName]: saved };
    if (rangeKey) {
      updates[rangeKey] = getInitialPaginationRange(saved);
      updates.currentPage = 0;
    }

    onSync(updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Syncs modal pagination with its persisted page size when opened.
 * @param {Object} params
 * @param {string} params.storageKey - localStorage key for this modal
 * @param {boolean} params.isOpen - Whether the modal is open
 * @param {number} params.itemsPerPage - Current modal page size in Redux
 * @param {Function} params.onSync - Callback invoked with fields to update in Redux
 * @param {string} params.itemsPerPageField - Redux items-per-page field name
 * @param {string} params.currentPageField - Redux current-page field name
 * @param {number} [params.fallback] - Fallback page size when nothing is stored
 */
export function useStoredModalItemsPerPageSync({
  storageKey,
  isOpen,
  itemsPerPage,
  onSync,
  itemsPerPageField,
  currentPageField,
  fallback,
}) {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const saved = getStoredItemsPerPage(storageKey, fallback);
    if (saved === itemsPerPage) {
      return;
    }

    onSync({
      [itemsPerPageField]: saved,
      [currentPageField]: 0,
    });
  }, [
    storageKey,
    isOpen,
    itemsPerPage,
    onSync,
    itemsPerPageField,
    currentPageField,
    fallback,
  ]);
}
